import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchLandingCampaign } from '@/lib/landing'
import { buildWinnerPageStyleVars } from '@/lib/campaign-winner-page-theme'
import { ApiClientError } from '@/lib/api/client'
import { getInstitutionBrandImageUrl } from '@/lib/profile-media'
import type { LandingCampaign, LandingCampaignResponse, LandingInstitution } from '@/types/landing'
import type { CampaignDrawWinnerStoredPayload } from '@/types/campaign-report'
import styles from './CampaignWinnerPublicPage.module.css'

function pickCampaignHeading(campaign: LandingCampaign): string {
  const t = campaign.title?.trim()
  if (t) return t
  return campaign.name?.trim() || 'Campanha'
}

function formatPeriod(startsAt: string | null, endsAt: string | null): string | null {
  if (!startsAt && !endsAt) return null
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  try {
    if (startsAt && endsAt) {
      const a = new Date(startsAt)
      const b = new Date(endsAt)
      if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
        return `${a.toLocaleDateString('pt-BR', opts)} — ${b.toLocaleDateString('pt-BR', opts)}`
      }
    }
    if (startsAt) {
      const a = new Date(startsAt)
      if (!Number.isNaN(a.getTime())) return `Início ${a.toLocaleDateString('pt-BR', opts)}`
    }
    if (endsAt) {
      const b = new Date(endsAt)
      if (!Number.isNaN(b.getTime())) return `Encerramento ${b.toLocaleDateString('pt-BR', opts)}`
    }
  } catch {
    return null
  }
  return null
}

function formatSavedAt(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatLotteryRef(draw: CampaignDrawWinnerStoredPayload): string {
  return draw.lottery_number_normalized
}

function labelMatchType(match: 'exact' | 'approximation'): string {
  return match === 'exact' ? 'Título exato' : 'Por aproximação'
}

function labelSearchMode(mode: 'exact' | 'approximation'): string {
  return mode === 'exact' ? 'Busca exata' : 'Busca por aproximação'
}

export function CampaignWinnerPublicPage() {
  const { slug } = useParams<{ slug: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [payload, setPayload] = useState<LandingCampaignResponse | null>(null)

  const load = useCallback(async () => {
    if (!slug) {
      setError('Link inválido.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchLandingCampaign(slug)
      setPayload(res)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.status === 404
            ? 'Campanha não encontrada.'
            : err.message
          : 'Não foi possível carregar os dados.'
      setError(message)
      setPayload(null)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  const campaign = payload?.data?.campaign ?? null
  const institution = payload?.data?.institution ?? null
  const drawWinner = campaign?.draw_winner ?? null

  const pageTitle = useMemo(() => {
    if (!campaign) return 'Sortee'
    const heading = pickCampaignHeading(campaign)
    const winnerName = drawWinner?.winner?.donor?.name?.trim()
    if (winnerName) {
      return `${winnerName} — Ganhador · ${heading}`
    }
    return `Resultado · ${heading}`
  }, [campaign, drawWinner])

  useEffect(() => {
    document.title = pageTitle
    return () => {
      document.title = 'Sortee'
    }
  }, [pageTitle])

  if (loading) {
    return (
      <div className={styles.shell}>
        <div className={styles.stateCenter}>
          <Icon icon="mdi:loading" className={styles.spinner} aria-hidden />
          <p className={styles.stateText}>Carregando resultado…</p>
        </div>
      </div>
    )
  }

  if (error || !campaign || !institution) {
    return (
      <div className={styles.shell}>
        <div className={styles.stateCenter}>
          <Icon icon="mdi:alert-circle-outline" className={styles.emptyVisual} aria-hidden />
          <h1 className={styles.stateTitle}>Não foi possível exibir</h1>
          <p className={styles.stateText}>{error ?? 'Dados indisponíveis.'}</p>
          <Link to="/" className={styles.campaignLink}>
            Ir para o início
          </Link>
        </div>
      </div>
    )
  }

  return (
    <WinnerContent
      slug={slug ?? ''}
      campaign={campaign}
      institution={institution}
      drawWinner={drawWinner}
    />
  )
}

interface WinnerContentProps {
  slug: string
  campaign: LandingCampaign
  institution: LandingInstitution
  drawWinner: CampaignDrawWinnerStoredPayload | null | undefined
}

function WinnerContent({ slug, campaign, institution, drawWinner }: WinnerContentProps) {
  const baseHref = `/campanha/${slug}`
  const heading = pickCampaignHeading(campaign)
  const institutionName = institution.corporate_name?.trim() || institution.name?.trim() || 'Instituição'
  const institutionBrandUrl = getInstitutionBrandImageUrl(institution)
  const period = formatPeriod(campaign.starts_at, campaign.ends_at)
  const prize = campaign.prize_description?.trim()

  const themeStyle = buildWinnerPageStyleVars(campaign)

  return (
    <div className={styles.shell} style={themeStyle}>
      <header className={styles.topBar}>
        <Link to={baseHref} className={styles.brand}>
          {institutionBrandUrl ? (
            <img src={institutionBrandUrl} alt={institutionName} className={styles.brandLogo} />
          ) : (
            <span className={styles.brandFallback} aria-hidden>
              {institutionName.slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className={styles.brandText}>
            <span className={styles.brandLabel}>Instituição</span>
            <span className={styles.brandName}>{institutionName}</span>
          </span>
        </Link>
        <Link to={baseHref} className={styles.campaignLink}>
          Ver campanha
          <Icon icon="mdi:arrow-right" width={18} height={18} aria-hidden />
        </Link>
      </header>

      <main className={styles.main}>
        {!drawWinner?.winner ? (
          <>
            <span className={styles.subKicker}>Sorteio</span>
            <h1 className={styles.kicker}>Resultado em breve</h1>
            <p className={styles.campaignTitle}>{heading}</p>
            {prize ? <p className={styles.prizeLine}>{prize}</p> : null}
            <div className={styles.stateCenter} style={{ padding: '2rem 0' }}>
              <Icon icon="mdi:trophy-broken" className={styles.emptyVisual} aria-hidden />
              <p className={styles.stateText}>
                O ganhador desta campanha ainda não foi divulgado. Volte mais tarde ou acompanhe a página da
                campanha.
              </p>
              <Link to={baseHref} className={styles.campaignLink}>
                Abrir página da campanha
              </Link>
            </div>
          </>
        ) : (
          <>
            <span className={styles.subKicker}>Resultado oficial</span>
            <h1 className={styles.kicker}>Ganhador</h1>
            <p className={styles.campaignTitle}>{heading}</p>
            {prize ? <p className={styles.prizeLine}>{prize}</p> : null}

            <article className={styles.heroCard}>
              <div className={styles.trophyRow} aria-hidden>
                <span className={styles.trophyIcon}>🏆</span>
              </div>
              <h2 className={styles.winnerName}>{drawWinner.winner.donor.name?.trim() || 'Ganhador'}</h2>

              <div className={styles.numberRing}>
                <span className={styles.numberLabel}>Cota vencedora</span>
                <span className={styles.numberValue}>#{drawWinner.winner.number}</span>
              </div>

              <ul className={styles.metaGrid}>
                <li className={styles.metaItem}>
                  <p className={styles.metaDt}>Referência do sorteio</p>
                  <p className={styles.metaDd}>{formatLotteryRef(drawWinner)}</p>
                </li>
                <li className={styles.metaItem}>
                  <p className={styles.metaDt}>Modalidade</p>
                  <p className={styles.metaDd}>{labelMatchType(drawWinner.match_type)}</p>
                </li>
                <li className={styles.metaItem}>
                  <p className={styles.metaDt}>Tipo de busca utilizada</p>
                  <p className={styles.metaDd}>{labelSearchMode(drawWinner.search_mode)}</p>
                </li>
                <li className={styles.metaItem}>
                  <p className={styles.metaDt}>Divulgado em</p>
                  <p className={styles.metaDd}>{formatSavedAt(drawWinner.saved_at)}</p>
                </li>
                {drawWinner.winner.match_type === 'approximation' && drawWinner.winner.distance > 0 ? (
                  <li className={`${styles.metaItem} ${styles.metaItemWide}`}>
                    <p className={styles.metaDt}>Distância em relação ao número de referência</p>
                    <p className={styles.metaDd}>{drawWinner.winner.distance}</p>
                  </li>
                ) : null}
              </ul>

              <ContactStrip donor={drawWinner.winner.donor} />
            </article>

            {period ? <p className={styles.periodNote}>Período da campanha: {period}</p> : null}
          </>
        )}
      </main>

      <footer className={styles.footerNote}>
        Sortee · página pública de resultado. As informações são fornecidas pela instituição responsável pela
        campanha.
      </footer>
    </div>
  )
}

function ContactStrip({
  donor,
}: {
  donor: { email?: string | null; phone?: string | null }
}) {
  const email = donor.email?.trim()
  const phone = donor.phone?.trim()
  if (!email && !phone) return null

  return (
    <div className={styles.contactBlock}>
      <p className={styles.contactTitle}>Contato informado</p>
      <div className={styles.contactLinks}>
        {email ? (
          <div className={styles.contactItem}>
            <span className={styles.contactLabel}>E-mail</span>
            <span className={styles.contactValue}>{email}</span>
          </div>
        ) : null}
        {phone ? (
          <div className={styles.contactItem}>
            <span className={styles.contactLabel}>Telefone</span>
            <span className={styles.contactValue}>{phone}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
